// src/services/invoiceService.js
import apiClient from './apiClient';

/**
 * Invoice service for API operations
 */
export const invoiceService = {
  /**
   * Get all invoices with optional filters
   * @param {Object} filters - Optional filters (status, clientId, limit, offset)
   * @returns {Promise<Object>} Response with invoices array
   */
  getAllInvoices: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await apiClient.get(`/invoices?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoices');
    }
  },

  /**
   * Get a specific invoice by ID
   * @param {string|number} invoiceId - The invoice ID
   * @returns {Promise<Object>} Response with invoice data
   */
  getInvoiceById: async (invoiceId) => {
    try {
      const response = await apiClient.get(`/invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Invoice not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch invoice');
    }
  },

  /**
   * Create a new invoice
   * @param {Object} invoiceData - Invoice data
   * @returns {Promise<Object>} Response with created invoice
   */
  createInvoice: async (invoiceData) => {
    try {
      const response = await apiClient.post('/invoices', invoiceData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create invoice');
    }
  },

  /**
   * Update an existing invoice
   * @param {string|number} invoiceId - The invoice ID
   * @param {Object} invoiceData - Updated invoice data
   * @returns {Promise<Object>} Response with updated invoice
   */
  updateInvoice: async (invoiceId, invoiceData) => {
    try {
      const response = await apiClient.put(`/invoices/${invoiceId}`, invoiceData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update invoice');
    }
  },

  /**
   * Delete an invoice
   * @param {string|number} invoiceId - The invoice ID
   * @returns {Promise<Object>} Response confirming deletion
   */
  deleteInvoice: async (invoiceId) => {
    try {
      const response = await apiClient.delete(`/invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete invoice');
    }
  },

  /**
   * Record a payment for an invoice
   * @param {string|number} invoiceId - The invoice ID
   * @param {Object} paymentData - Payment data (amount, paymentDate, paymentMethod, etc.)
   * @returns {Promise<Object>} Response with updated invoice
   */
  recordPayment: async (invoiceId, paymentData) => {
    try {
      const response = await apiClient.post(`/invoices/${invoiceId}/payments`, paymentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to record payment');
    }
  }
};

export default invoiceService;
